import React, { useEffect, useState } from 'react';
import { ref, set } from 'firebase/database';
import { toast } from 'react-toastify';
import {
    FiClock,
    FiGlobe,
    FiSave,
} from 'react-icons/fi';

import { db } from '../../firebase';
import { countryOfIp } from '../../utils/ipCountry';
import CustomDropdown from '../CustomDropdown';

// Overview tab — the "what is this device?" view.
//
// Engineered to fit on one viewport without scrolling on a typical
// desktop. General Information is the tallest card so it spans two grid
// rows on the left, freeing the right column to stack System then Time
// & Region.
//
//   Row 1   GeneralInfoCard ┐  SystemCard
//   Row 2                   ┘  TimeRegionCard
//
// Controls + Diagnostics moved to their own "Controls" tab so Overview
// stays purely "what is this device?".
//
// Each card is presentational and reads from the live `device` snapshot
// passed in by the page; mutations go straight to RTDB.

export default function OverviewTab({ mac, device, ipToCountry, groupList }) {
    return (
        // `items-stretch` lets the card surfaces fill their row tracks so
        // the right column's System + Time & Region stack matches the
        // height of the (taller) General Information card on the left.
        // Cards pass `h-full` through to their inner section so the
        // chrome actually expands instead of leaving empty grid space.
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <div className="lg:row-span-2 h-full">
                <GeneralInfoCard
                    mac={mac}
                    device={device}
                    ipToCountry={ipToCountry}
                    groupList={groupList}
                />
            </div>
            <SystemCard device={device} />
            <TimeRegionCard mac={mac} device={device} />
        </div>
    );
}

// ---------------------------------------------------------------------
// Generic card primitives
// ---------------------------------------------------------------------

function Card({ title, headerRight, children, className = '' }) {
    return (
        <section className={`card-chrome p-5 shadow-sm h-full ${className}`}>
            <header className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-brand-400">{title}</h2>
                {headerRight}
            </header>
            <div className="space-y-2.5">{children}</div>
        </section>
    );
}

// One label/value row. Caller decides whether to render it (so missing
// fields can be hidden by the parent rather than fed undefined here).
function Row({ label, children }) {
    return (
        <div className="grid grid-cols-[140px_1fr] items-start gap-3 text-sm">
            <span className="text-gray-400">{label}</span>
            <span className="text-gray-200">{children}</span>
        </div>
    );
}

// Thin separator between functional groups inside a card. Used to give
// long row-lists a bit of visual rhythm without heavy section headings.
function Divider({ className = 'my-1' }) {
    return <div className={`${className} border-t border-white/[0.04]`} aria-hidden="true" />;
}

// Hardware "spec tile" — small bordered well showing one fact about the
// device. Designed to read at a glance in a 2×2 grid: tiny uppercase
// label up top, value below in the primary text colour, optional hint.
// `title` becomes the hover tooltip — useful when the value gets
// truncated (e.g. long Android build IDs in System Version).
function HwTile({ label, value, hint, mono, title }) {
    const present = value != null && value !== '' && value !== '—';
    return (
        <div className="card-inset p-3">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
            <div
                className={`mt-1 text-sm font-medium truncate ${present ? 'text-white' : 'text-gray-500'} ${mono ? 'font-mono' : ''}`}
                title={title || (typeof value === 'string' ? value : undefined)}
            >
                {present ? value : '—'}
            </div>
            {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
        </div>
    );
}

// ---------------------------------------------------------------------
// 1) General Information — identity & address only.
//    Hardware specs / versions / OTA live in SystemCard.
// ---------------------------------------------------------------------

function GeneralInfoCard({ mac, device, ipToCountry, groupList }) {
    const country = countryOfIp(ipToCountry, device?.ip_address);
    const groupName = device?.group ? groupList[device.group]?.name : null;

    return (
        <Card title="General Information">
            {/* Identity — auto values pulled from the player (read-only). Manual backup
                labels live on the License tab's Manual labels card. */}
            <div className="grid grid-cols-2 gap-2">
                <HwTile label="Device Name" value={device?.name} />
                <HwTile label="Customer" value={device?.business_name} />
            </div>

            <Divider className="my-3" />

            {/* Network / address — same 2-col tile grid as System's
                hardware tiles for a unified visual rhythm. */}
            <div className="grid grid-cols-2 gap-2">
                <HwTile label="MAC Address" value={mac} mono />
                <HwTile label="IP Address" value={device?.ip_address} mono />
                <HwTile
                    label="Country"
                    value={country ? (
                        <span className="inline-flex items-center gap-2 min-w-0">
                            {country.country_code && (
                                <img
                                    src={`https://flagcdn.com/24x18/${country.country_code.toLowerCase()}.png`}
                                    alt=""
                                    className="w-4 h-3 object-cover shrink-0"
                                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                            )}
                            <span className="truncate">{country.country_name || 'Unknown'}</span>
                        </span>
                    ) : null}
                    title={country?.country_name}
                />
                <HwTile label="Group" value={groupName} />
            </div>

            <Divider className="my-3" />

            {/* Versions — System Version build IDs can be long, so the
                tile carries the value verbatim and exposes it in full
                via tooltip when truncated. */}
            <div className="grid grid-cols-2 gap-2">
                <HwTile
                    label="System Version"
                    value={device?.system_version}
                    mono
                />
                <HwTile
                    label="MS Version"
                    value={device?.monitoring_service_version}
                />
            </div>
        </Card>
    );
}

// ---------------------------------------------------------------------
// 2) System — hardware identity at a glance. Software versions live in
//    GeneralInfoCard, OTA toggle lives in ControlsCard, diagnostic
//    actions live in DiagnosticsCard.
// ---------------------------------------------------------------------

function SystemCard({ device }) {
    const dd = device?.device_data || {};

    const manufacturer = dd.device_manufacturer;
    const hwModel = dd.device_model || device?.device_model;
    const androidVersion = dd.android_version;
    const androidSdk = dd.android_sdk;
    const screen = dd.screen_resolution;

    return (
        <section className="card-chrome p-5 shadow-sm h-full">
            <header className="mb-4">
                <h2 className="text-base font-semibold text-brand-400">System</h2>
            </header>

            {/* Hardware tile grid — 2×2, Apple "spec sticker" style. */}
            <div className="grid grid-cols-2 gap-2">
                <HwTile label="Manufacturer" value={manufacturer} />
                <HwTile label="Model" value={hwModel} />
                <HwTile
                    label="Android"
                    value={androidVersion}
                    hint={androidSdk != null ? `SDK ${androidSdk}` : null}
                />
                <HwTile
                    label="Screen"
                    value={screen ? screen.replace('x', '×') : null}
                    mono
                />
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------
// 4) Time & Region — full-width, three-column horizontal layout.
//
//    Hero (current state) on the left · change-zone in the middle ·
//    change-format on the right. Stacks on smaller screens.
//
//    Reads tz / format from device_data, writes back via the
//    `set_timezone:` / `set_time_format:` commands documented in
//    DEVICE-TIME-ZONE.md §4.
// ---------------------------------------------------------------------

const COMMON_TIMEZONES = [
    { id: 'Europe/Belgrade',     label: 'Europe / Belgrade'     },
    { id: 'Europe/Berlin',       label: 'Europe / Berlin'       },
    { id: 'Europe/Madrid',       label: 'Europe / Madrid'       },
    { id: 'Europe/Paris',        label: 'Europe / Paris'        },
    { id: 'Europe/London',       label: 'Europe / London'       },
    { id: 'Europe/Athens',       label: 'Europe / Athens'       },
    { id: 'America/New_York',    label: 'America / New York'    },
    { id: 'America/Chicago',     label: 'America / Chicago'     },
    { id: 'America/Los_Angeles', label: 'America / Los Angeles' },
    { id: 'Asia/Tokyo',          label: 'Asia / Tokyo'          },
    { id: 'Asia/Dubai',          label: 'Asia / Dubai'          },
    { id: 'Australia/Sydney',    label: 'Australia / Sydney'    },
];

function TimeRegionCard({ mac, device }) {
    const currentTz = device?.device_data?.time_zone || '';
    const currentFormat = device?.device_data?.time_format || '24';

    const [tzDraft, setTzDraft] = useState(currentTz);
    const [formatDraft, setFormatDraft] = useState(currentFormat);
    const [otherTzMode, setOtherTzMode] = useState(false);
    const [savingTz, setSavingTz] = useState(false);
    const [savingFormat, setSavingFormat] = useState(false);

    useEffect(() => {
        setTzDraft(currentTz);
        const isOther = currentTz && !COMMON_TIMEZONES.some((t) => t.id === currentTz);
        setOtherTzMode(Boolean(isOther));
    }, [currentTz]);
    useEffect(() => { setFormatDraft(currentFormat); }, [currentFormat]);

    const sendCommand = async (cmd) => {
        await set(ref(db, `mdm/devices/${mac}/command`), cmd);
    };

    const saveTimezone = async () => {
        if (!tzDraft || tzDraft === currentTz) return;
        try {
            setSavingTz(true);
            await sendCommand(`set_timezone:${tzDraft}`);
            toast.info(`Sent: set_timezone:${tzDraft}`);
        } catch (err) {
            toast.error(`Failed to send timezone: ${err.message}`);
        } finally {
            setSavingTz(false);
        }
    };

    const saveTimeFormat = async (next) => {
        if (next === currentFormat) return;
        try {
            setSavingFormat(true);
            setFormatDraft(next); // optimistic
            await sendCommand(`set_time_format:${next}`);
            toast.info(`Sent: set_time_format:${next}`);
        } catch (err) {
            toast.error(`Failed to send time format: ${err.message}`);
            setFormatDraft(currentFormat); // rollback
        } finally {
            setSavingFormat(false);
        }
    };

    const tzDirty = tzDraft && tzDraft !== currentTz;
    const prettyTz = currentTz ? currentTz.replace('_', ' ').replace('/', ' / ') : null;

    return (
        <section className="card-chrome p-5 shadow-sm h-full">
            <header className="mb-4">
                <h2 className="text-base font-semibold text-brand-400">Time & Region</h2>
            </header>

            {/* Horizontal layout — hero on the left, edit row on the
                right. The grid uses a 1:1.4 ratio so the picker has room
                to breathe; everything stacks on small screens. */}
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3 items-stretch">
                {/* Hero — current state */}
                <div className="card-inset p-4 flex items-center">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="brand-icon-badge shrink-0"><FiGlobe /></div>
                        <div className="min-w-0">
                            <div className="text-base font-medium text-white truncate">
                                {prettyTz || <span className="text-gray-500">Unknown timezone</span>}
                            </div>
                            <div className="text-xs text-gray-400 inline-flex items-center gap-1.5">
                                <FiClock />
                                {currentFormat}-hour clock
                            </div>
                        </div>
                    </div>
                </div>

                {/* Edit row — picker + Send + format toggle, all
                    bottom-aligned so they sit on the same line. */}
                <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[220px] space-y-2">
                        <label className="text-[11px] uppercase tracking-wider text-gray-500">Change time zone</label>
                        {!otherTzMode ? (
                            <CustomDropdown
                                value={tzDraft}
                                onChange={(value) => {
                                    if (value === '__other__') {
                                        setOtherTzMode(true);
                                        setTzDraft('');
                                    } else {
                                        setTzDraft(value);
                                    }
                                }}
                                options={[
                                    ...COMMON_TIMEZONES.map(({ id, label }) => ({ value: id, label })),
                                    { value: '__other__', label: 'Other…' },
                                ]}
                                placeholder="— select —"
                            />
                        ) : (
                            <div className="flex flex-wrap items-center gap-2">
                                <input
                                    value={tzDraft}
                                    onChange={(e) => setTzDraft(e.target.value)}
                                    placeholder="IANA id (e.g. America/Toronto)"
                                    className="bg-white/[0.05] border border-white/[0.08] rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand/40 min-w-[200px] flex-1"
                                />
                                <button
                                    onClick={() => { setOtherTzMode(false); setTzDraft(currentTz); }}
                                    className="text-xs text-gray-400 hover:text-gray-200"
                                >
                                    Use list
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={saveTimezone}
                        disabled={!tzDirty || savingTz}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand hover:bg-brand-400 text-white text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <FiSave /> {savingTz ? 'Sending…' : 'Send'}
                    </button>

                    {/* Format toggle — no separate label, the 12h / 24h
                        glyph carries the meaning. Sits flush with the
                        dropdown row via items-end on the parent. */}
                    <div
                        className="inline-flex items-center bg-white/[0.05] border border-white/[0.08] rounded-full p-0.5"
                        role="group"
                        aria-label="Time format"
                    >
                        {['12', '24'].map((opt) => (
                            <button
                                key={opt}
                                onClick={() => saveTimeFormat(opt)}
                                disabled={savingFormat}
                                aria-pressed={formatDraft === opt}
                                title={`${opt}-hour clock`}
                                className={`px-3.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                                    formatDraft === opt
                                        ? 'bg-brand text-white shadow-[0_0_0_1px_rgba(235,94,0,0.4)]'
                                        : 'text-gray-400 hover:text-white'
                                }`}
                            >
                                {opt}h
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

